'use client'

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'

type Theme = 'dark' | 'light' | 'system'
type FontSize = 'comfortable' | 'large' | 'extra-large' | 'accessibility'
type FontFamily = 'modern' | 'classic' | 'typewriter'
type AccentColor = 'amber' | 'emerald' | 'rose' | 'sapphire'

type PreferencesProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  defaultFontSize?: FontSize
  defaultFontFamily?: FontFamily
  defaultAccentColor?: AccentColor
  storageKey?: string
}

type PreferencesProviderState = {
  // Theme preferences
  theme: Theme
  setTheme: (theme: Theme) => void
  actualTheme: 'dark' | 'light' // The resolved theme (not 'system')
  
  // Typography preferences
  fontSize: FontSize
  setFontSize: (fontSize: FontSize) => void
  
  // Font family preferences
  fontFamily: FontFamily
  setFontFamily: (fontFamily: FontFamily) => void
  
  // Accent color preferences
  accentColor: AccentColor
  setAccentColor: (accentColor: AccentColor) => void
}

const initialState: PreferencesProviderState = {
  // Theme
  theme: 'light',
  setTheme: () => null,
  actualTheme: 'light',
  
  // Typography
  fontSize: 'comfortable',
  setFontSize: () => null,
  
  // Font family
  fontFamily: 'modern',
  setFontFamily: () => null,
  
  // Accent color
  accentColor: 'amber',
  setAccentColor: () => null
}

const PreferencesProviderContext = createContext<PreferencesProviderState>(initialState)

export function PreferencesProvider({
  children,
  defaultTheme = 'light',
  defaultFontSize = 'comfortable',
  defaultFontFamily = 'modern',
  defaultAccentColor = 'amber',
  storageKey = 'infinite-pages-preferences',
  ...props
}: PreferencesProviderProps) {
  // Theme state
  const [theme, setTheme] = useState<Theme>(defaultTheme)
  const [actualTheme, setActualTheme] = useState<'dark' | 'light'>('light')
  
  // Typography state
  const [fontSize, setFontSize] = useState<FontSize>(defaultFontSize)
  
  // Font family state
  const [fontFamily, setFontFamily] = useState<FontFamily>(defaultFontFamily)
  
  // Accent color state
  const [accentColor, setAccentColor] = useState<AccentColor>(defaultAccentColor)

  // Save preferences to localStorage
  const savePreferences = useCallback(() => {
    try {
      const preferences = {
        theme,
        fontSize,
        fontFamily,
        accentColor
      }
      localStorage.setItem(storageKey, JSON.stringify(preferences))
    } catch (error) {
      console.warn('Failed to save preferences to localStorage:', error)
    }
  }, [theme, fontSize, fontFamily, accentColor, storageKey])

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const preferences = JSON.parse(stored)
        
        if (preferences.theme) setTheme(preferences.theme)
        if (preferences.fontSize) setFontSize(preferences.fontSize)
        if (preferences.fontFamily) setFontFamily(preferences.fontFamily)
        if (preferences.accentColor) setAccentColor(preferences.accentColor)
      }
    } catch (error) {
      console.warn('Failed to load preferences from localStorage:', error)
    }
  }, [storageKey])

  // Apply theme changes
  useEffect(() => {
    const root = window.document.documentElement

    // Remove all theme classes
    root.classList.remove('light', 'dark')

    let resolvedTheme: 'dark' | 'light'

    if (theme === 'system') {
      // Check system preference
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      resolvedTheme = systemTheme
    } else {
      resolvedTheme = theme
    }

    // Apply the resolved theme
    root.classList.add(resolvedTheme)
    setActualTheme(resolvedTheme)
  }, [theme, storageKey])

  // Apply font size changes
  useEffect(() => {
    const root = window.document.documentElement

    // Remove all font size classes
    root.classList.remove('font-comfortable', 'font-large', 'font-extra-large', 'font-accessibility')

    // Apply new font size class
    root.classList.add(`font-${fontSize}`)
  }, [fontSize])

  // Apply font family changes
  useEffect(() => {
    const root = window.document.documentElement

    // Remove all font family classes
    root.classList.remove('font-modern', 'font-classic', 'font-typewriter')

    // Apply new font family class
    root.classList.add(`font-${fontFamily}`)
  }, [fontFamily])

  // Apply accent color changes
  useEffect(() => {
    const root = window.document.documentElement

    // Remove all accent color classes
    root.classList.remove('accent-amber', 'accent-emerald', 'accent-rose', 'accent-sapphire')

    // Apply new accent color class
    root.classList.add(`accent-${accentColor}`)
  }, [accentColor])

  // CRITICAL FIX: Separate effect for saving preferences
  // This prevents the cascade where savePreferences changes trigger all other effects
  useEffect(() => {
    savePreferences()
  }, [theme, fontSize, fontFamily, accentColor, savePreferences])

  // Listen for system theme changes when using 'system' mode
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

      const handleChange = () => {
        const root = window.document.documentElement
        root.classList.remove('light', 'dark')
        const systemTheme = mediaQuery.matches ? 'dark' : 'light'
        root.classList.add(systemTheme)
        setActualTheme(systemTheme)
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    // Add explicit return for non-system case
    return undefined
  }, [theme])

  // CRITICAL FIX: Memoize setter functions to prevent recreation on every render
  // This prevents cascading re-renders across all context consumers
  const setThemeCallback = useCallback((theme: Theme) => {
    setTheme(theme)
  }, [])

  const setFontSizeCallback = useCallback((fontSize: FontSize) => {
    setFontSize(fontSize)
  }, [])

  const setFontFamilyCallback = useCallback((fontFamily: FontFamily) => {
    setFontFamily(fontFamily)
  }, [])

  const setAccentColorCallback = useCallback((accentColor: AccentColor) => {
    setAccentColor(accentColor)
  }, [])

  // CRITICAL FIX: Memoize the context value object
  // Only recreate when actual state values change, not on every render
  // This prevents ALL context consumers from re-rendering unnecessarily
  const value = useMemo(() => ({
    // Theme
    theme,
    setTheme: setThemeCallback,
    actualTheme,

    // Typography
    fontSize,
    setFontSize: setFontSizeCallback,

    // Font family
    fontFamily,
    setFontFamily: setFontFamilyCallback,

    // Accent color
    accentColor,
    setAccentColor: setAccentColorCallback
  }), [theme, actualTheme, fontSize, fontFamily, accentColor, setThemeCallback, setFontSizeCallback, setFontFamilyCallback, setAccentColorCallback])

  return (
    <PreferencesProviderContext.Provider {...props} value={value}>
      {children}
    </PreferencesProviderContext.Provider>
  )
}

// Convenience hooks
export const usePreferences = () => {
  const context = useContext(PreferencesProviderContext)

  if (context === undefined)
    throw new Error('usePreferences must be used within a PreferencesProvider')

  return context
}

// Theme hooks (backward compatibility)
export const useTheme = () => {
  const { theme, setTheme, actualTheme } = usePreferences()
  return { theme, setTheme, actualTheme }
}

// Convenience hook for just checking if dark mode is active
export const useIsDark = () => {
  const { actualTheme } = usePreferences()
  return actualTheme === 'dark'
}

// Typography hooks
export const useFontSize = () => {
  const { fontSize, setFontSize } = usePreferences()
  return { fontSize, setFontSize }
}

// Font family hooks
export const useFontFamily = () => {
  const { fontFamily, setFontFamily } = usePreferences()
  return { fontFamily, setFontFamily }
}

// Accent color hooks
export const useAccentColor = () => {
  const { accentColor, setAccentColor } = usePreferences()
  return { accentColor, setAccentColor }
}